import { cloneDeep, filter, get, isEmpty, isNil, toLower, head, includes, isEqual, size, remove, find, findIndex } from 'lodash'
import { THIRD_PARTY_TYPES } from '@/utils/constants'
import helpers from './helpers'

const getters = {
    getNavigation: (_state, _getters, rootState, rootGetters) => () => {
        const _categories = helpers.buildCategories(rootState, rootGetters)
        return helpers.buildCriteriaList(_categories, rootState)
    },

    getFirstPartyCategories: (_state, _getters, rootState) => () => {
        const _criteriaList = {
            customTaxonomies: get(rootState, 'data[customTaxonomies]', {}),
            media: get(rootState, 'data[media]', {})
        }

        return isEmpty(_criteriaList.customTaxonomies.list) && isEmpty(_criteriaList.media.list) ? [] : [{ myData: _criteriaList }]
    },

    /**
     *
     * @param {Object} _state unused but imposed by vuex
     * @param {Object} _getters unused but imposed by vuex
     * @param {Object} _rootState unused but imposed by vuex
     * @param {Object} rootGetters
     * @returns
     */
    getSharingCategories: (_state, _getters, _rootState, rootGetters) => () => {
        const taxonomies = rootGetters['data/taxonomies/getSharedTaxonomies']()
        const audiences = rootGetters['data/audience/getSharedAudiences']()

        return Object.assign(
            {},
            (!isEmpty(taxonomies) || !isEmpty(audiences)) && {
                sharing: Object.assign(
                    {},
                    !isEmpty(taxonomies) && { taxonomies: { list: taxonomies, type: 'taxonomies', subType: 'sharing' } },
                    !isEmpty(audiences) && { audiences: { list: audiences, type: 'sharedAudiences', subType: 'sharing' } }
                )
            }
        )
    },

    /**
     * Builds the different categories of the criteria list for positive events on a cclone
     *
     * @param {Object} _state unused but imposed by vuex
     * @param {Object} getters
     * @param {Object} rootState
     */
    getPositiveEvents: (_state, getters, rootState) => () => {
        const firstPartyCategories = getters.getFirstPartyCategories()
        const audiences = { audience: rootState.data.audience }
        const sharingCategories = getters.getSharingCategories()
        const events = [...firstPartyCategories, Object.assign({}, !isEmpty(audiences.audience.list) && { audiences }), sharingCategories]

        return events.filter(e => !isEmpty(e))
    },

    getThirdPartyCategories: (_state, _getters, rootState, rootGetters) => () => {
        const getSettingValue = settingName => {
            return rootGetters['data/settings/getSocioDemoValue']({ settingName })
        }

        const _categories = {
            demographic: getSettingValue('demographic') && !isEmpty(rootState.data.demographic.list),
            behavioral: getSettingValue('behavioral') && !isEmpty(rootState.data.behavioral.list)
        }

        let _criteriaList = {}

        for (const _categoryKey in _categories) {
            if (_categories[_categoryKey]) {
                _criteriaList = {
                    ..._criteriaList,
                    [_categoryKey]: get(rootState, `data[${_categoryKey}]`, {})
                }
            }
        }

        return isEmpty(_criteriaList) ? [] : [{ thirdParty: _criteriaList }]
    },

    /**
     * Builds the different categories of the criteria list for universe on a cclone
     *
     * @param {Object} _state unused but imposed by vuex
     * @param {Object} getters
     */
    getUniverse: (_state, getters) => () => [...getters.getPositiveEvents(), ...getters.getThirdPartyCategories()],

    getTreeNode: (state, getters, rootState, rootGetters) => params => {
        const criterion = isNil(params.criterion) ? toLower(state.menuCriteriaType) : params.criterion
        let data
        if (criterion === 'audience') data = rootGetters['data/audience/getActiveAudiences']({})
        else if (criterion === 'sharedAudiences') data = rootGetters['data/audience/getSharedAudiences']()
        else if (criterion === 'taxonomies') data = rootGetters['data/taxonomies/getSharedTaxonomies']()
        else if (criterion === 'georadar') data = [...rootState.data.georadar.savedPoints, ...rootGetters['data/georadar/getActiveItems']({})]
        else if (criterion === 'hierarchy') {
            if (params.criterionSubType === 'sharing') {
                data = Object.values(rootState.data.taxonomies.shared[rootState.data.taxonomies.sharedSelectedId].segments)
            } else {
                data = rootState.data.customTaxonomies.hierarchies[rootState.data.customTaxonomies.selected]
            }
        } else if (criterion === 'secondParty' || criterion === 'secondparty') data = rootState.data.secondParty.activeList[params.criterionSubType].list
        else data = rootState.data[criterion].list
        if (!isNil(data)) {
            let treeNode = filter(data, elem => {
                return elem.parentJsonKeyName === params.jsonKeyName
            })
            if (criterion === 'behavioral') {
                const _wgt = remove(treeNode, node => node.id === 'wb_gt')
                treeNode = _wgt.concat(treeNode)
            } else if (criterion === 'media') {
                // avoid double deletion when opening media criterion
                const hasConversions = find(treeNode, item => item.id === 'conversion')
                if (hasConversions && rootGetters['data/media/getIsConversionEmpty']({})) {
                    const conversionIndex = findIndex(treeNode, item => item.id === 'conversion')
                    treeNode.splice(conversionIndex, 1)
                } else if (rootGetters['data/media/getIsCampaignEmpty']({})) {
                    const campaingIndex = findIndex(treeNode, item => item.id === '"campaign"')
                    treeNode.splice(campaingIndex, 1)
                }
            }
            return treeNode
        }
        return []
    },

    /**
     * Find and returns all child and deep child of a criterion
     *
     * @param {Number} level - Deep level limit for retrieve descendants
     * @param {Object} node
     * @param {String} category
     * @param {String|Array|Number} [returnsKey]
     * @returns {Object[]}
     */
    getDescendants: (_state, getters, rootState, rootGetters) => ({ level, node, category, returnsKey }) => {
        let descendants = []
        const nextLevel = level === Infinity ? Infinity : level - 1
        let criteriaList
        if (node.jsonKey.type === 'CustomSegment' || node.jsonKey.type === 'SmartSegment') {
            // We use two getters here for retrieve all segments (from client account and shared)
            // TODO: refactor when customTaxonomies and taxonomies (shared) will be combined into one module.
            criteriaList = [...rootGetters['data/customTaxonomies/getAllHierarchies'](), ...rootGetters['data/taxonomies/getAllSegments']()]
        } else if (node.jsonKey.type !== 'SecondPartyCluster') {
            criteriaList = rootState.data[category].list
        } else {
            criteriaList = rootState.data.secondParty.activeList[node.jsonKey.parentName].list
        }

        for (const criterion of criteriaList) {
            if (node.jsonKey.name === criterion.parentJsonKeyName) {
                const descendant = returnsKey ? get(criterion, returnsKey) : criterion
                descendants.push(descendant)
                if (criterion.hasChild && nextLevel > 0) {
                    descendants = [
                        ...descendants,
                        ...getters.getDescendants({
                            level: nextLevel,
                            node: criterion,
                            category,
                            returnsKey
                        })
                    ]
                }
            }
        }
        return descendants
    },

    /**
     * Finds all children and deep children of a criterion and returns their names
     *
     * @param {Number} level
     * @param {Object} node
     * @param {String} category
     * @param {String|Array|Number} [returnsKey]
     * @returns {String[]}
     */
    getDescendantsJsonKeyNames: (_state, getters) => params => {
        return getters.getDescendants({ ...params, returnsKey: 'jsonKey.name' })
    },

    getFirstPartyData: (_state, _getters, rootState) => () => {
        let firstPartyData = []

        //manually picking data Type as global constant FIRST_PARTY tries to fresh .list objects that no longer exist
        const firstPartyDataType = ['media', 'customTaxonomies']
        for (const firstPartyType of firstPartyDataType) {
            firstPartyData = [...firstPartyData, ...rootState.data[firstPartyType].list]
        }
        return firstPartyData
    },

    getThirdPartyOnlyInAudienceFormula: (_state, getters, rootState) => ({ formula }) => {
        let _formula = cloneDeep(formula)
        if (!Array.isArray(_formula)) {
            _formula = [_formula] // make sure formula is an Array
        }
        if (typeof head(_formula) === 'string') {
            _formula.shift() //remove AND/OR symbol
        }
        const _thirdPartyElementsInFormula = []
        for (const box of _formula) {
            if (includes(THIRD_PARTY_TYPES, box.type)) {
                _thirdPartyElementsInFormula.push(box.type)
            } else if (box.type === 'Audience') {
                const audience = find(rootState.data.audience.list, a => a.id === box.ids[0])
                if (audience) {
                    const isThirdPartyOnly = getters.getThirdPartyOnlyInAudienceFormula({ formula: audience.formula })
                    if (isThirdPartyOnly) _thirdPartyElementsInFormula.push(box.type)
                }
            }
        }
        return !isEmpty(_thirdPartyElementsInFormula) && isEqual(size(_thirdPartyElementsInFormula), size(_formula))
    }
}

export default getters
